import React, { useState, useEffect } from 'react';
import { FaEye, FaExternalLinkAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import SummaryApi from '../common/index';
import AWS from 'aws-sdk';

AWS.config.update({
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  region: process.env.REACT_APP_AWS_REGION,
});
const s3 = new AWS.S3();

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('*');
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [filters, setFilters] = useState([{ name: 'All', filter: '*' }]);
  const [showForm, setShowForm] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    filterClass: '',
    website: '',
    description: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const adminUser = localStorage.getItem('admin');
    setIsLoggedIn(!!adminUser);
  }, []);

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    fetch(SummaryApi.getCategories.url)
      .then((res) => res.json())
      .then((categories) => {
        setCategories(categories);
        const filterList = categories.map((cat) => ({
          name: cat.category,
          filter: cat.filterClass,
        }));
        setFilters([{ name: 'All', filter: '*' }, ...filterList]);
      });
  }, []);

  const fetchItems = () => {
    fetch(SummaryApi.getPortfolioItem.url)
      .then((res) => res.json())
     .then((data) => {
  const items = Array.isArray(data) ? data : data?.items;
  if (Array.isArray(items)) {
    setPortfolioItems(items);
  } else {
    console.error("Unexpected response format:", data);
    setPortfolioItems([]); // fallback
  }
})
      .catch((err) => console.error('Fetch portfolio failed:', err));
  };

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const uploadImageToS3 = async (file) => {
    const params = {
      Bucket: process.env.REACT_APP_BUCKET_NAME,
      Key: `portfolio_images/${Date.now()}_${file.name}`,
      Body: file,
      ContentType: file.type,
    };

    return new Promise((resolve, reject) => {
      s3.upload(params, (err, data) => {
        if (err) reject(err);
        else resolve(data.Location);
      });
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile) return;

    try {
      const imgSrc = await uploadImageToS3(imageFile);

      const payload = {
        ...formData,
        imgSrc,
      };

      const res = await fetch(SummaryApi.createPortfolioItem.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Failed to add item');

      await res.json();
      fetchItems();
      setShowForm(false);
      setFormData({
        title: '',
        category: '',
        filterClass: '',
        website: '',
        description: '',
      });
      setImageFile(null);
    } catch (err) {
      console.error('Add failed:', err);
    }
  };

  const filteredItems = portfolioItems.filter(
    (item) => activeFilter === '*' || item.filterClass === activeFilter
  );

  return (
    <section className="pt-10 px-4 sm:px-10" aria-labelledby="portfolio-heading">
      <div className="container mx-auto">
        <header className="text-center mb-12 px-4 sm:px-28">
          <h2 id="portfolio-heading" className="text-3xl font-bold mb-4 text-gray-800">
            Our Portfolio
          </h2>
          <p className="text-sm sm:text-lg text-gray-600">
            Explore our diverse range of completed projects, from web and app development to immersive designs.
          </p>
          {isLoggedIn && (
            <button
              onClick={() => setShowForm(!showForm)}
              className="mt-6 px-6 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700 transition"
            >
              {showForm ? 'Close Form' : 'Create New Item'}
            </button>
          )}
        </header>

        {showForm && (
          <form
            onSubmit={handleSubmit}
            className="max-w-3xl mx-auto mb-10 bg-white p-6 rounded shadow"
            encType="multipart/form-data"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                name="title"
                placeholder="Title"
                value={formData.title}
                onChange={handleChange}
                className="border p-2 rounded"
                required
              />
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="border p-2 rounded"
                required
              >
                <option value="">Select Category</option>
                {categories.map((cat, idx) => (
                  <option key={idx} value={cat.category}>{cat.category}</option>
                ))}
              </select>
              <input
                type="text"
                name="filterClass"
                placeholder="Filter Class (e.g., web-dev)"
                value={formData.filterClass}
                onChange={handleChange}
                className="border p-2 rounded"
                required
              />
              <input
                type="text"
                name="website"
                placeholder="Website URL"
                value={formData.website}
                onChange={handleChange}
                className="border p-2 rounded"
              />
              <input
                type="file"
                name="image"
                accept="image/*"
                onChange={handleFileChange}
                className="border p-2 rounded"
                required
              />
            </div>
            <textarea
              name="description"
              placeholder="Description"
              value={formData.description}
              onChange={handleChange}
              className="border p-2 rounded w-full mt-4"
              rows={4}
              required
            />
            <button
              type="submit"
              className="mt-4 px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
            >
              Submit
            </button>
          </form>
        )}

        <div className="mb-8">
          <ul className="flex flex-wrap justify-center space-x-2 sm:space-x-4 mb-8">
            {filters.map((filter, index) => (
              <li
                key={index}
                className={`cursor-pointer py-2 px-4 ${
                  activeFilter === filter.filter
                    ? 'text-blue-500 font-bold underline'
                    : 'text-gray-600'
                }`}
                onClick={() => handleFilterClick(filter.filter)}
              >
                {filter.name}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-wrap -mx-2 sm:-mx-4 justify-center">
          {filteredItems.map((item, index) => (
            <article
              key={index}
              className="w-full sm:w-1/2 lg:w-1/3 px-2 sm:px-4 mb-8 portfolio-item"
            >
              <div className="relative bg-white border border-gray-200 rounded-lg overflow-hidden shadow-lg">
                <figure className="relative">
                  <img
                    src={item.imgSrc}
                    alt={`Screenshot of ${item.title}`}
                    className="w-full h-60 object-cover"
                  />
                  <Link
                    to={item.website}
                    target="_blank"
                    className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center bg-black bg-opacity-50 text-white opacity-0 hover:opacity-100 transition-opacity"
                  >
                    <FaEye className="text-2xl text-blue-500" />
                  </Link>
                  <a
                    href={item.website}
                    className="absolute top-2 right-2 bg-white rounded-full p-2 shadow-lg"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaExternalLinkAlt className="text-gray-600" />
                  </a>
                </figure>
                <div className="p-4 text-center">
                  <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.category}</p>
                  <p className="text-gray-500 text-sm mt-2">{item.description}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
